// Keep literal code examples untouched while recognizing Pocket blocks and bullets.
export function parseJournalPocket(source = '') {
  const parts = [];
  let lines = [];
  let fence = null;
  const flush = () => {
    if (lines.length) parts.push({ type: 'markdown', text: lines.join('\n') });
    lines = [];
  };
  const input = String(source).split('\n');
  for (let i = 0; i < input.length; i++) {
    const line = input[i];
    const marker = line.match(/^ {0,3}(`{3,}|~{3,})/);
    if (fence) {
      lines.push(line);
      if (marker && marker[1][0] === fence[0] && marker[1].length >= fence.length && /^ {0,3}(`+|~+)\s*$/.test(line)) fence = null;
      continue;
    }
    if (marker) { fence = marker[1]; lines.push(line); continue; }
    const opening = line.match(/^ {0,3}<pocket:(timeline|decision-tree|chart|flowchart)\b([^>]*)>(.*)$/i);
    if (opening) {
      const closing = new RegExp(`</pocket:${opening[1]}>`, 'i');
      const end = input.findIndex((candidate, index) => index >= i && closing.test(candidate));
      if (end !== -1) {
        flush();
        const closingMatch = input[end].match(closing);
        const raw = [...input.slice(i, end), input[end].slice(0, closingMatch.index + closingMatch[0].length)].join('\n');
        const title = opening[2].match(/\btitle\s*=\s*(?:"([^"]*)"|'([^']*)')/i);
        const chartType = opening[2].match(/\btype\s*=\s*(?:"([^"]*)"|'([^']*)')/i);
        const body = [opening[3], ...input.slice(i + 1, end + 1)].join('\n');
        parts.push({ type: opening[1].toLowerCase(), chartType: (chartType?.[1] ?? chartType?.[2] ?? '').toLowerCase(), title: title?.[1] ?? title?.[2] ?? '', text: body.split(closing)[0].trim(), raw });
        const trailing = input[end].split(closing)[1];
        if (trailing?.trim()) lines.push(trailing);
        i = end;
        continue;
      }
    }
    lines.push(/^ {4}|^\t/.test(line) ? line : line.replace(/^(\s*)•\s*/, '$1- '));
  }
  flush();
  return parts;
}

export function parsePocketChart(text) {
  const rows = [];
  // Numeric values delimit rows even when Pocket exports the whole chart on one line.
  const pattern = /([^|]+)\|\s*([+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:e[+-]?\d+)?)(?=\s|$)/gi;
  let end = 0;
  for (const match of text.matchAll(pattern)) {
    if (text.slice(end, match.index).trim()) return null;
    const label = match[1].trim();
    const value = Number(match[2]);
    if (!label || !Number.isFinite(value)) return null;
    rows.push({ label, value });
    end = match.index + match[0].length;
  }
  return rows.length && !text.slice(end).trim() ? rows : null;
}

export function parsePocketTimeline(text) {
  return text.split('\n').filter((line) => line.trim()).map((line) => {
    const [period, title, ...description] = line.split('|');
    return { period: period.trim(), title: (title || '').trim(), description: description.join('|').trim() };
  });
}

export function parsePocketFlowchart(text) {
  const rows = String(text).split('\n').filter((line) => line.trim());
  const paths = rows.map((line) => line.split(/->|→/).map((label) => label.trim()));
  // Preserve invalid input visibly rather than silently dropping steps.
  return paths.length && paths.every((path) => path.length > 1 && path.every(Boolean)) ? paths : null;
}

export function parsePocketDecisionTree(text) {
  const nodes = [];
  for (const line of text.split('\n')) {
    const heading = line.match(/^\s*([^\s:]+)::(.*)$/);
    if (heading) { nodes.push({ id: heading[1], title: heading[2].trim(), description: [], branches: [] }); continue; }
    const node = nodes.at(-1);
    if (!node || !line.trim()) continue;
    const branch = line.match(/^\s*[•*-]\s*(.*?)\s*=>\s*(\S+)\s*$/);
    if (branch) node.branches.push({ label: branch[1], target: branch[2] });
    else node.description.push(line.trim());
  }
  return nodes;
}
